import { useState, useMemo, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { MoreHorizontal, Plus, X } from "lucide-react"
import { toast } from "sonner"
import type { ReportList, ReportListItem } from "@/types/report-list"
import type { ReportResponse } from "@/types/report"
import { getReportListMenuItems, type ContextMenuCommand } from "@/lib/context-menu-items"
import {
  useReportListItems,
  useUpdateReportListItem,
  useReorderReportListItems,
  useNormalizeDisplayOrders,
  hasDuplicateDisplayOrders,
} from "@/hooks/use-report-list-items"
import { useHydratedReportListItems } from "@/hooks/use-hydrated-report-list-items"
import { isReportListItemInactive } from "@/lib/report-list-helpers"
import { ReportListGrid } from "./report-list-grid"
import { ReportListSkeleton } from "./report-list-skeleton"
import { createDefaultDeliveryUpdateDto } from "@/lib/delivery-defaults"
import {
  DEFAULT_DELIVERY_TYPE_KEY,
  DEFAULT_FILE_TYPE_KEY,
  DEFAULT_COLLATION_TYPE_KEY,
  resolveGuid,
} from "@/lib/constants"
import { useTenantIdNumber } from "@/hooks/use-tenants"

interface ReportListSectionProps {
  openTabs: ReportList[]
  activeTabIndex: number
  onTabChange: (index: number) => void
  onCloseTab: (reportListKey: string) => void
  onCreateClick: () => void
  onOpenClick: () => void
  onDuplicateClick: () => void
  onRenameClick: () => void
  onDeleteClick: () => void
  onDeliveryConfigClick?: () => void
  onTabRename: (reportListKey: string, newName: string) => void
  selectedItems: ReportListItem[]
  onSelectionChange: (items: ReportListItem[]) => void
  activeItem: ReportListItem | null
  onActiveItemChange: (item: ReportListItem | null) => void
  onItemContextAction: (command: ContextMenuCommand, item: ReportListItem) => void
  reportResponses: ReportResponse[]
}

export function ReportListSection({
  openTabs,
  activeTabIndex,
  onTabChange,
  onCloseTab,
  onCreateClick,
  onOpenClick,
  onDuplicateClick,
  onRenameClick,
  onDeleteClick,
  onDeliveryConfigClick,
  onTabRename,
  selectedItems,
  onSelectionChange,
  activeItem,
  onActiveItemChange,
  onItemContextAction,
  reportResponses,
}: Readonly<ReportListSectionProps>) {
  const { t } = useTranslation()
  const tenantId = useTenantIdNumber()

  // Get localized menu items
  const reportListMenuItems = useMemo(() => getReportListMenuItems(t), [t])

  // Fetch items for the active tab
  const activeTab = openTabs[activeTabIndex]
  const { data: itemResponses = [], isLoading } = useReportListItems(
    activeTab?.reportListKey || "",
  )
  const updateItem = useUpdateReportListItem()
  const reorderMutation = useReorderReportListItems()
  const normalizeDisplayOrdersMutation = useNormalizeDisplayOrders()

  // Self-heal lists with duplicate displayOrder values (e.g. from older data);
  // tracked per list key so a failed attempt doesn't retry every render.
  const normalizedListKeysRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const listKey = activeTab?.reportListKey
    if (!listKey || !hasDuplicateDisplayOrders(itemResponses)) return
    if (normalizedListKeysRef.current.has(listKey)) return

    normalizedListKeysRef.current.add(listKey)
    normalizeDisplayOrdersMutation.mutate({ items: itemResponses, listKey })
  }, [activeTab?.reportListKey, itemResponses, normalizeDisplayOrdersMutation])

  // Inline tab rename state
  const [editingTabKey, setEditingTabKey] = useState<string | null>(null)
  const [editingTabName, setEditingTabName] = useState("")

  const handleStartTabRename = (tab: ReportList) => {
    setEditingTabKey(tab.reportListKey)
    setEditingTabName(tab.name)
  }

  const handleSaveTabRename = (tab: ReportList) => {
    const trimmed = editingTabName.trim()
    if (trimmed && trimmed !== tab.name) {
      onTabRename(tab.reportListKey, trimmed)
    }
    setEditingTabKey(null)
    setEditingTabName("")
  }

  const handleCancelTabRename = () => {
    setEditingTabKey(null)
    setEditingTabName("")
  }

  // Build a map of reportKey -> report path for resolving paths on items
  // where item.report?.path is not populated by the API
  const reportPathMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of reportResponses) {
      map.set(r.reportKey, r.path)
    }
    return map
  }, [reportResponses])

  // Hydrate items with full parameter data (including display names)
  const {
    items,
    isLoading: isParameterSummaryLoading,
  } = useHydratedReportListItems(itemResponses, reportPathMap)

  const reportInactiveDateMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const report of reportResponses) {
      if (report.inactiveDateTime) {
        map.set(report.reportKey, report.inactiveDateTime)
      }
    }
    return map
  }, [reportResponses])

  const listItemsWithInactiveState = useMemo(
    () =>
      items.map((item) => {
        const inactiveDateTime = isReportListItemInactive(item, reportResponses)
          ? reportInactiveDateMap.get(item.reportKey)
          : undefined

        if (!item.report) {
          return {
            ...item,
            report: {
              reportKey: item.reportKey,
              name: item.displayName,
              path: reportPathMap.get(item.reportKey) || "",
              tenantId: item.tenantId,
              parameters: [],
              inactiveDateTime,
            },
          }
        }

        return {
          ...item,
          report: {
            ...item.report,
            inactiveDateTime,
          },
        }
      }),
    [items, reportResponses, reportInactiveDateMap, reportPathMap],
  )

  const handleContextMenuAction = (command: string) => {
    // Don't execute if no tabs are open (for disabled items)
    if (openTabs.length === 0 && ["duplicate", "rename", "delete"].includes(command)) {
      return
    }

    switch (command) {
      case "duplicate":
        onDuplicateClick()
        break
      case "rename":
        onRenameClick()
        break
      case "delete":
        onDeleteClick()
        break
      case "configureDelivery":
        onDeliveryConfigClick?.()
        break
      case "previewAll":
      case "saveAllPdf":
      case "deleteSelected":
        // Propagate to parent via item context action with all items
        onItemContextAction(command as ContextMenuCommand, listItemsWithInactiveState[0])
        break
    }
  }

  const handleItemUpdate = (item: ReportListItem) => {
    // For now, use placeholder values for required delivery fields
    updateItem.mutate({
      reportListItem: {
        reportListItemKey: item.reportListItemKey,
        reportListKey: item.reportListKey,
        reportKey: item.reportKey,
        reportDeliveryKey: item.reportDeliveryKey,
        displayName: item.displayName,
        displayOrder: item.displayOrder,
        reportListItemParameters: item.reportListItemParameters,
        lastModifiedUserKey: item.lastModifiedUserKey || "system",
        tenantId,
      },
      reportDelivery: createDefaultDeliveryUpdateDto({
        reportDeliveryKey: item.reportDeliveryKey,
        reportDeliveryTypeKey: resolveGuid(item.reportDelivery?.reportDeliveryTypeKey, DEFAULT_DELIVERY_TYPE_KEY),
        reportFileTypeKey: resolveGuid(item.reportDelivery?.reportFileTypeKey, DEFAULT_FILE_TYPE_KEY),
        reportCollationTypeKey: resolveGuid(item.reportDelivery?.reportCollationTypeKey, DEFAULT_COLLATION_TYPE_KEY),
        createdUserKey: item.createdUserKey || "system",
        lastModifiedUserKey: item.lastModifiedUserKey || "system",
        tenantId,
      }),
    })
  }

  const handleItemsReorder = (reorderedItems: ReportListItem[]) => {
    // Build a map of previous display orders to determine what changed
    const previousOrderMap = new Map<string, number>();
    for (const item of listItemsWithInactiveState) {
      previousOrderMap.set(item.reportListItemKey, item.displayOrder);
    }

    // Only send updates for items whose order actually changed
    const changedItems = reorderedItems.filter(
      (item) =>
        previousOrderMap.get(item.reportListItemKey) !== item.displayOrder,
    );

    // Persist reorder to backend with both full list (for optimistic update)
    // and changed items (for efficient API calls)
    reorderMutation.mutate(
      { items: reorderedItems, changedItems },
      {
        onError: () => {
          toast.error("Failed to update report order")
        },
      },
    )
  }

  const renderTabContent = (index: number) => {
    if (index !== activeTabIndex) return null
    if (isLoading) return <ReportListSkeleton />

    return (
      <ReportListGrid
        items={listItemsWithInactiveState}
        isParameterSummaryLoading={isParameterSummaryLoading}
        selectedItems={selectedItems}
        onSelectionChange={onSelectionChange}
        activeItem={activeItem}
        onActiveItemChange={onActiveItemChange}
        onItemUpdate={handleItemUpdate}
        onItemsReorder={handleItemsReorder}
        onContextAction={onItemContextAction}
      />
    )
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b px-3 py-2">
        {/* Title on left */}
        <h2 className="text-sm font-semibold">{t("reportList.title")}</h2>

        {/* Buttons on right */}
        <div className="flex items-center gap-2">
          <Button onClick={onCreateClick} size="sm">
            <Plus className="mr-1 h-4 w-4" />
            {t("reportList.create")}
          </Button>
          <Button onClick={onOpenClick} size="sm" variant="outline">
            {t("reportList.open")}
          </Button>
          <TooltipProvider>
            <Tooltip>
              <DropdownMenu>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" disabled={openTabs.length === 0} aria-label="More actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <DropdownMenuContent>
                  {reportListMenuItems.map((item) => {
                    if (item.separator) {
                      return <DropdownMenuSeparator key={`${item.command}-separator`} />
                    }

                    // Determine if item should be disabled when no list is open
                    const isDisabled = openTabs.length === 0 &&
                      ["duplicate", "rename", "delete"].includes(item.command)

                    // Get tooltip for disabled state
                    const getDisabledTooltip = (command: string) => {
                      if (command === "duplicate") return t("reportList.tooltips.duplicateDisabled")
                      if (command === "rename") return t("reportList.tooltips.renameDisabled")
                      if (command === "delete") return t("reportList.tooltips.deleteDisabled")
                      return undefined
                    }

                    return (
                      <DropdownMenuItem
                        key={item.command}
                        className={item.destructive ? "text-destructive" : ""}
                        disabled={isDisabled}
                        title={isDisabled ? getDisabledTooltip(item.command) : undefined}
                        onClick={() => handleContextMenuAction(item.command)}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipContent>
                {t("reportList.tooltips.moreOptions")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Tabs */}
      {openTabs.length > 0 ? (
        <Tabs
          value={String(activeTabIndex)}
          onValueChange={(v) => onTabChange(Number(v))}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <TabsList className="h-auto w-full justify-start gap-0 rounded-none border-b bg-transparent p-0">
            {openTabs.map((tab, index) => {
              const isActive = index === activeTabIndex
              return (
              <div key={tab.reportListKey} className="group/tab relative">
                <ContextMenu>
                  <ContextMenuTrigger asChild>
                    <TabsTrigger
                      value={String(index)}
                      onDoubleClick={() => handleStartTabRename(tab)}
                      className={cn(
                        "h-auto flex-none rounded-none !border-x-0 !border-t-0 !border-b-3 !bg-transparent py-2 pl-4 pr-7 !shadow-none transition-colors",
                        isActive
                          ? "!border-b-primary !bg-muted! !font-semibold !text-primary"
                          : "!border-b-transparent !text-muted-foreground",
                      )}
                    >
                      {editingTabKey === tab.reportListKey ? (
                        <Input
                          value={editingTabName}
                          onChange={(e) => setEditingTabName(e.target.value)}
                          onBlur={() => handleSaveTabRename(tab)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveTabRename(tab)
                            if (e.key === "Escape") handleCancelTabRename()
                            e.stopPropagation()
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          className="h-6 w-32"
                        />
                      ) : (
                        tab.name
                      )}
                    </TabsTrigger>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    {reportListMenuItems.map((item) =>
                      item.separator ? (
                        <ContextMenuSeparator key={item.command} />
                      ) : (
                        <ContextMenuItem
                          key={item.command}
                          className={item.destructive ? "text-destructive" : ""}
                          onClick={() => handleContextMenuAction(item.command)}
                        >
                          {item.label}
                        </ContextMenuItem>
                      ),
                    )}
                  </ContextMenuContent>
                </ContextMenu>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onCloseTab(tab.reportListKey)
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label={`Close ${tab.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              )
            })}
          </TabsList>
          {openTabs.map((tab, index) => (
            <TabsContent
              key={tab.reportListKey}
              value={String(index)}
              className="flex-1 overflow-hidden"
            >
              {renderTabContent(index)}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">{t("reportList.noListsOpen")}</p>
            <p className="mt-1 text-xs">{t("reportList.getStarted")}</p>
          </div>
        </div>
      )}
    </div>
  )
}
